// Pure helpers for the date filter on the SMS Status number tabs
// (components/sms/DateRangeFilter.js). A range is { from, to }: "YYYY-MM-DD"
// day keys in the viewer's local time, both inclusive, either one "" for
// open-ended.

export const EMPTY_RANGE = { from: "", to: "" };

const pad = (n) => String(n).padStart(2, "0");

export const toDayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// "2026-09-01" -> local midnight, without the UTC shift new Date("2026-09-01") gives.
export const parseDayKey = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ""));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};

export const isRangeSet = (range) => Boolean(range && (range.from || range.to));

/** Whether a timestamp falls on a day inside the range. No range: everything matches. */
export const inDateRange = (iso, range) => {
  if (!isRangeSet(range)) return true;
  const t = iso ? new Date(iso).getTime() : NaN;
  if (!Number.isFinite(t)) return false;
  const start = parseDayKey(range.from);
  if (start && t < start.getTime()) return false;
  const end = parseDayKey(range.to);
  if (end) {
    end.setDate(end.getDate() + 1);
    if (t >= end.getTime()) return false;
  }
  return true;
};

/** Rows whose `field` timestamp is inside the range. */
export const filterByDate = (rows, field, range) =>
  isRangeSet(range) ? rows.filter((row) => inDateRange(row[field], range)) : rows;

const shortDay = (key) =>
  parseDayKey(key).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const dateRangeLabel = (range) => {
  if (!isRangeSet(range)) return "Any date";
  const { from, to } = range;
  if (from && to) return from === to ? shortDay(from) : `${shortDay(from)} – ${shortDay(to)}`;
  return from ? `Since ${shortDay(from)}` : `Until ${shortDay(to)}`;
};

const daysAgo = (today, n) => {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  d.setDate(d.getDate() - n);
  return d;
};

export const DATE_PRESETS = [
  { key: "today", label: "Today", range: (t) => ({ from: toDayKey(t), to: toDayKey(t) }) },
  { key: "yesterday", label: "Yesterday", range: (t) => ({ from: toDayKey(daysAgo(t, 1)), to: toDayKey(daysAgo(t, 1)) }) },
  { key: "7d", label: "Last 7 days", range: (t) => ({ from: toDayKey(daysAgo(t, 6)), to: toDayKey(t) }) },
  { key: "30d", label: "Last 30 days", range: (t) => ({ from: toDayKey(daysAgo(t, 29)), to: toDayKey(t) }) },
  {
    key: "month",
    label: "This month",
    range: (t) => ({ from: toDayKey(new Date(t.getFullYear(), t.getMonth(), 1)), to: toDayKey(t) }),
  },
  {
    key: "lastMonth",
    label: "Last month",
    range: (t) => ({
      from: toDayKey(new Date(t.getFullYear(), t.getMonth() - 1, 1)),
      to: toDayKey(new Date(t.getFullYear(), t.getMonth(), 0)),
    }),
  },
];

/** Clicking a day: the first click starts a range, the second ends it (in either order). */
export const pickDay = (range, key) => {
  if (!range.from || range.to) return { from: key, to: "" };
  return key < range.from ? { from: key, to: range.from } : { from: range.from, to: key };
};

/** Weeks of a month, Sunday first; days outside the month are null. */
export const monthGrid = (year, month) => {
  const first = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => toDayKey(new Date(year, month, i + 1)))];
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};
